import { useToastProvider } from "@deere/fuel-react";
import { useOktaAuth } from "@okta/okta-react";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BusinessAction } from "../../api/rbac/types";
import returnsApi from "../../api/returns/returnsApi";
import {
  Returns,
  ReturnsRequestV2WithoutUrlAndToken,
  ReturnsResponse,
} from "../../api/returns/returnsInterfaces";
import useReturnsApi from "../../api/returns/useReturnsApi";
import { ConfigContext } from "../../context/ConfigContext";
import { DealerAccountContext } from "../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../context/UserContext/UserContext";
import { CATEGORIES, TOGGLES } from "../../pages/featureToggles/constants";
import { isAuthorizedCorporateUser } from "../../pages/preferences/utils";
import { dayJSFormatterForAPI } from "../../pages/returns/list/reducer/common";

export const useReturnsNotifier = () => {
  const { authState } = useOktaAuth();
  const currentDate = dayjs();
  const [needActionCount, setNeedActionCount] = useState(0);
  const [needActionReturns, setNeedActionReturns] = useState<Returns[]>([]);
  const [returnNotificationSeen, setReturnNotificationSeen] = useState(false);
  const {
    RETURNS_SERVICE_URL,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_RETURNS_SERVICE,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
  } = useContext(ConfigContext);
  const { dealerAccount, selectedAccounts } = useContext(DealerAccountContext);
  const { isRBACEnabled, isCorporateViewEnabled } =
    useContext(RBACAccessContext);
  const user = useContext(UserContext);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const returnsServiceApi = useReturnsApi();

  const currentUserIsCorporateManager =
    isCorporateViewEnabled &&
    isAuthorizedCorporateUser(user, ENABLE_CORPORATE_VIEW_DPA_ROLE);
  const accountId = dealerAccount?.id as string;
  const accountIds =
    currentUserIsCorporateManager && selectedAccounts.length > 1
      ? selectedAccounts.map((account) => account.id)
      : [accountId];

  const isDailyReturnFTEnabled = isToggleEnabled(
    CATEGORIES.RETURNS,
    TOGGLES.ENABLE_RETURNS
  );
  const isReturnApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_RETURNS_SERVICE;
  const isAuthorizedToViewReturn =
    !isRBACEnabled || !!user?.isAuthorizedTo(BusinessAction.VIEW_RETURNS);
  const isAppNotificationsEnabled = isToggleEnabled(
    CATEGORIES.ALERTS_SERVICE,
    TOGGLES.ENABLE_ALERTS_AND_NOTIFICATIONS
  );
  const isSurplusReturnsEnabled = isToggleEnabled(
    CATEGORIES.RETURNS,
    TOGGLES.ENABLE_SURPLUS_RETURNS
  );
  const { enqueueToast } = useToastProvider();
  const { t } = useTranslation("common");

  async function getAllNeedActionAlerts(pageSize: number = 10) {
    let response: ReturnsResponse | undefined;
    const commonRequestParams = {
      startDate: dayJSFormatterForAPI(
        currentDate.add(1, "day").subtract(3, "months")
      ),
      endDate: dayJSFormatterForAPI(currentDate),
      returnType: isSurplusReturnsEnabled ? "ALL" : "ZCRR",
      includeStatusCount: true,
      status: "needAction",
      pageNumber: 0,
      pageSize,
      withLeadingZeros: true,
    };

    try {
      const request: ReturnsRequestV2WithoutUrlAndToken = {
        ...commonRequestParams,
        accountIds,
      };
      response = isReturnApiAuthEnable
        ? await returnsServiceApi.getAllReturnsV2(request)
        : await returnsApi.getAllReturnsV2({
            ...request,
            returnsServiceUrl: RETURNS_SERVICE_URL,
            accessToken: authState?.accessToken?.accessToken ?? "",
          });
    } catch (error) {
      console.error("Error fetching returns:", error);
      return {
        returns: [],
        statusCount: { needAction: 0 },
      };
    }

    return response;
  }

  const handler = useCallback(async () => {
    let response: ReturnsResponse = await getAllNeedActionAlerts(25);
    const needActionReturnsCount = response.statusCount?.needAction ?? 0;
    if (needActionReturnsCount > response.returns.length) {
      response = await getAllNeedActionAlerts(needActionReturnsCount);
    }

    if (needActionReturnsCount) {
      setNeedActionCount(needActionReturnsCount);
      setNeedActionReturns(response.returns);

      if (!isAppNotificationsEnabled) {
        enqueueToast({
          variant: "success",
          message: t("notifications.returns.toast.message"),
          isCloseable: true,
        });
      }
    }
  }, [accountId, selectedAccounts]);

  useEffect(() => {
    if (isDailyReturnFTEnabled && isAuthorizedToViewReturn) {
      handler();
    }
  }, [accountId, selectedAccounts]);
  return {
    needActionCount,
    returnNotificationSeen,
    setReturnNotificationSeen,
    needActionReturns,
  };
};

export const hasReturnNotification = (
  needActionCount: number,
  returnNotificationSeen: boolean
) => hasNeedActionReturnAvailable(needActionCount) && !returnNotificationSeen;

export const hasNeedActionReturnAvailable = (needActionCount: number) =>
  needActionCount > 0;
